import { useEffect, useRef, useState } from "react";
import { AnimatePresence, MotionConfig, motion, useMotionValueEvent, useScroll, useTransform, type MotionValue } from "framer-motion";
import { MonitorPlay } from "lucide-react";
import { Badge } from "../ui/badge";
import ShinyText from "../reactbits/ShinyText";
import { PRO_FEATURES, type ProFeature } from "../features/proFeatures";

/*
 * A scroll-driven tour of the Quilt Pro screens (the same live screens as /features). On wide
 * screens the list stays pinned on the left and the screen on the right while the page scrolls;
 * each stretch of scrolling hands over to the next screen. Phones get the screens one after another.
 */

const ease = [0.22, 1, 0.36, 1] as const;

function useWide() {
    const query = "(min-width: 1024px)";
    const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
    useEffect(() => {
        const mq = window.matchMedia(query);
        const onChange = () => setWide(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    return wide;
}

function Heading() {
    return (
        <div className="mx-auto max-w-3xl px-4 text-center">
            <Badge variant="outline" className="mb-3 border-purple-500/30 bg-purple-500/10 px-4 py-2 md:mb-4">
                <MonitorPlay className="mr-2 h-4 w-4 text-purple-500" />
                <ShinyText text="Inside Quilt Pro" speed={3} className="font-medium text-purple-500" />
            </Badge>
            <h2 className="mb-3 text-3xl font-bold text-slate-900 md:mb-6 md:text-5xl dark:text-white">A Look Inside Quilt Pro</h2>
            <p className="mx-auto max-w-2xl text-base text-slate-600 md:text-lg dark:text-slate-400">
                The tools your team uses to manage patients, shape every video and approve it before it reaches them.
            </p>
        </div>
    );
}

/** One entry in the pinned list: its bar fills as the page scrolls through its stretch. */
function TourItem({ feature, index, active, progress, onSelect }: { feature: ProFeature; index: number; active: boolean; progress: MotionValue<number>; onSelect: () => void }) {
    const fill = useTransform(progress, (p) => Math.min(1, Math.max(0, p * PRO_FEATURES.length - index)));
    const Icon = feature.icon;
    return (
        <li className="relative pl-6">
            <span aria-hidden className="absolute bottom-1 left-0 top-1 w-0.5 overflow-hidden rounded-full bg-slate-900/10 dark:bg-white/10">
                <motion.span className="block h-full w-full origin-top bg-[linear-gradient(180deg,#F725B1,#8E0EF0)]" style={{ scaleY: fill }} />
            </span>
            <button
                type="button"
                onClick={onSelect}
                aria-current={active ? "step" : undefined}
                className={`w-full rounded-xl py-1 text-left transition-opacity duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${active ? "opacity-100" : "opacity-45 hover:opacity-75"}`}
            >
                <span className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 dark:text-purple-300">
                    <Icon className="size-4" />
                    {feature.badge}
                </span>
                <span className="mt-1.5 block text-xl font-semibold text-slate-900 xl:text-2xl dark:text-white">{feature.title}</span>
                <motion.span
                    className="block overflow-hidden text-base text-slate-600 dark:text-slate-400"
                    initial={false}
                    animate={{ height: active ? "auto" : 0, opacity: active ? 1 : 0, marginTop: active ? 8 : 0 }}
                    transition={{ duration: 0.45, ease }}
                >
                    {feature.text}
                </motion.span>
            </button>
        </li>
    );
}

function PinnedTour() {
    const track = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({ target: track, offset: ["start start", "end end"] });
    const [active, setActive] = useState(0);

    useMotionValueEvent(scrollYProgress, "change", (p) => {
        setActive(Math.min(PRO_FEATURES.length - 1, Math.max(0, Math.floor(p * PRO_FEATURES.length))));
    });

    // Jump to the start of a screen's stretch
    const select = (i: number) => {
        const el = track.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY;
        const span = el.offsetHeight - window.innerHeight;
        window.scrollTo({ top: top + (span * (i + 0.15)) / PRO_FEATURES.length, behavior: "smooth" });
    };

    const { Mock, id } = PRO_FEATURES[active];

    return (
        // Each screen gets one viewport of scrolling
        <div ref={track} style={{ height: `${PRO_FEATURES.length * 100}svh` }}>
            <div className="sticky top-0 flex h-[100svh] items-center pt-16">
                <div className="mx-auto grid w-full max-w-7xl grid-cols-[minmax(0,0.75fr)_minmax(0,1.6fr)] items-center gap-10 px-6 xl:gap-14">
                    <ol className="flex flex-col gap-7">
                        {PRO_FEATURES.map((f, i) => (
                            <TourItem key={f.id} feature={f} index={i} active={i === active} progress={scrollYProgress} onSelect={() => select(i)} />
                        ))}
                    </ol>
                    {/* Screens cross over in the same grid cell, so the outgoing one fades while the next rises */}
                    <div className="grid">
                        <AnimatePresence initial={false}>
                            <motion.div
                                key={id}
                                className="[grid-area:1/1]"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0, scale: 0.96, filter: "blur(4px)", transition: { duration: 0.45 } }}
                                transition={{ duration: 0.5 }}
                            >
                                <Mock reserve="9rem" />
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StackedTour() {
    return (
        <div className="mt-10 flex flex-col gap-16 px-4">
            {PRO_FEATURES.map(({ id, icon: Icon, badge, title, text, Mock }) => (
                <div key={id}>
                    <div className="mx-auto mb-6 max-w-xl text-center">
                        <span className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 dark:text-purple-300">
                            <Icon className="size-4" />
                            {badge}
                        </span>
                        <h3 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">{title}</h3>
                        <p className="mt-2 text-base text-slate-600 dark:text-slate-400">{text}</p>
                    </div>
                    <Mock />
                </div>
            ))}
        </div>
    );
}

export function ProductTour() {
    const wide = useWide();
    return (
        <MotionConfig reducedMotion="user">
            <div className="w-full pt-10 md:pt-20">
                <Heading />
                {wide ? <PinnedTour /> : <StackedTour />}
            </div>
        </MotionConfig>
    );
}

export default ProductTour;
